import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { EquipmentTagPicker } from "./EquipmentTagPicker";
import { IconPicker } from "./IconPicker";
import type { Equipment, EquipmentInsert } from "@/hooks/useEquipment";

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  short_name: z.string().optional(),
  manufacturer: z.string().optional(),
  icon: z.string().optional(),
  new_price: z.coerce.number().optional().nullable(),
  rental_price: z.coerce.number().optional().nullable(),
  carnet_ata_value: z.coerce.number().optional().nullable(),
  weight: z.coerce.number().optional().nullable(),
  carnet_ata_weight_unit: z.string().optional(),
  is_serialized: z.boolean(),
  stock_quantity: z.coerce.number().optional().nullable(),
  country_of_origin: z.string().optional(),
  carnet_ata_description: z.string().optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface EquipmentFormProps {
  equipment?: Equipment;
  onSubmit: (data: EquipmentInsert, tagIds: string[]) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const EquipmentForm = ({
  equipment,
  onSubmit,
  onCancel,
  isLoading,
}: EquipmentFormProps) => {
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(
    equipment?.tags?.map((t) => t.id) || [],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: equipment?.name || "",
      short_name: equipment?.short_name || "",
      manufacturer: equipment?.manufacturer || "",
      icon: equipment?.icon || "",
      new_price: equipment?.new_price ? Number(equipment.new_price) : null,
      rental_price: equipment?.rental_price
        ? Number(equipment.rental_price)
        : null,
      carnet_ata_value:
        equipment?.carnet_ata_value != null
          ? Number(equipment.carnet_ata_value)
          : null,
      weight: equipment?.weight != null ? Number(equipment.weight) : null,
      carnet_ata_weight_unit: equipment?.carnet_ata_weight_unit || "kg",
      is_serialized: equipment?.is_serialized ?? true,
      stock_quantity: equipment?.stock_quantity ?? 0,
      country_of_origin: equipment?.country_of_origin || "",
      carnet_ata_description: equipment?.carnet_ata_description || "",
      notes: equipment?.notes || "",
    },
  });

  const isSerialized = form.watch("is_serialized");

  const handleSubmit = (values: FormValues) => {
    onSubmit(
      {
        name: values.name,
        short_name: values.short_name || null,
        manufacturer: values.manufacturer || null,
        icon: values.icon || null,
        new_price: values.new_price,
        rental_price: values.rental_price,
        carnet_ata_value: values.carnet_ata_value,
        weight: values.weight,
        carnet_ata_weight_unit: values.carnet_ata_weight_unit || "kg",
        is_serialized: values.is_serialized,
        stock_quantity: values.is_serialized ? 0 : values.stock_quantity || 0,
        country_of_origin: values.country_of_origin || null,
        carnet_ata_description: values.carnet_ata_description || null,
        notes: values.notes || null,
      },
      selectedTagIds,
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="manufacturer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Manufacturer</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    data-testid="equipment-manufacturer-input"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name *</FormLabel>
                <FormControl>
                  <Input {...field} data-testid="equipment-name-input" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="short_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Short Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Optional short name for compact display"
                  data-testid="equipment-short-name-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="icon"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Icon</FormLabel>
              <FormControl>
                <IconPicker value={field.value} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="new_price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New Price</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="equipment-new-price-input"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rental_price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rental Price</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="equipment-rental-price-input"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4 rounded-lg border p-4">
          <div>
            <h3 className="text-sm font-medium">Carnet ATA</h3>
            <p className="text-sm text-muted-foreground">
              Used when exporting project equipment for customs.
            </p>
          </div>

          <FormField
            control={form.control}
            name="carnet_ata_description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>ATA Description</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Description shown on the Carnet ATA list"
                    data-testid="equipment-carnet-ata-description-input"
                  />
                </FormControl>
                <FormDescription>
                  If empty, the export uses manufacturer and name.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country_of_origin"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Country of Origin</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g. Switzerland"
                    data-testid="equipment-country-of-origin-input"
                  />
                </FormControl>
                <FormDescription>
                  Used for URSPRUNGSLAND in the Carnet ATA export.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="carnet_ata_value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Value for Carnet ATA</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid="equipment-carnet-ata-value-input"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    If empty, the export uses the new price.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="weight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit Weight kg</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid="equipment-weight-input"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="carnet_ata_weight_unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Weight Unit</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="kg"
                      data-testid="equipment-carnet-ata-weight-unit-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <FormField
          control={form.control}
          name="is_serialized"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel>Serialized Equipment</FormLabel>
                <p className="text-sm text-muted-foreground">
                  {field.value
                    ? "Each instance has a unique identifier"
                    : "Track stock quantity only"}
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="equipment-serialized-switch"
                />
              </FormControl>
            </FormItem>
          )}
        />

        {!isSerialized && (
          <FormField
            control={form.control}
            name="stock_quantity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Stock Quantity</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    data-testid="equipment-stock-quantity-input"
                    {...field}
                    value={field.value ?? 0}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <div className="space-y-2">
          <FormLabel>Tags</FormLabel>
          <EquipmentTagPicker
            selectedTagIds={selectedTagIds}
            onChange={setSelectedTagIds}
          />
        </div>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea {...field} rows={4} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            data-testid="equipment-submit"
          >
            {equipment ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
